import {parseJSON} from '../Common/json';
import {createLogger} from '../Common/logger';

const LOGGER = createLogger('PageRenderReady');

export const renderEventsUrl = (pageId, pageVersion) => {
    const urlSearchParams = new URLSearchParams();
    if (pageVersion) {
        urlSearchParams.set('version', pageVersion);
    }

    const queryString = urlSearchParams.toString();
    return `/pages/${pageId}/events${queryString ? `?${queryString}` : ''}`;
};

export const parseHtmlDocument = html => new DOMParser().parseFromString(html, 'text/html');

export const replacePageContent = ({document, html, parseHtml = parseHtmlDocument}) => {
    const nextDocument = parseHtml(html);
    const currentMain = document.querySelector('main.page-content');
    const nextMain = nextDocument.querySelector('main.page-content');
    if (!currentMain || !nextMain) {
        return null;
    }

    const replacementMain = document.importNode ? document.importNode(nextMain, true) : nextMain;
    currentMain.replaceWith(replacementMain);

    const nextTitle = nextDocument.querySelector('title');
    if (nextTitle) {
        document.title = nextTitle.textContent;
    }

    return replacementMain;
};

export const setupRenderReady = ({
    document = window.document,
    EventSource = window.EventSource,
    fetch = window.fetch.bind(window),
    location = window.location,
    onLoaded = () => {
    },
    parseHtml = parseHtmlDocument,
} = {}) => {
    const pending = document.querySelector('[data-page-render-pending="true"]');
    if (!pending || !EventSource) {
        return null;
    }

    const pageId = document.querySelector('meta[name="swf-page-id"]')?.getAttribute('content');
    const pageVersion = pending.getAttribute('data-page-version') ||
        document.querySelector('meta[name="swf-page-version"]')?.getAttribute('content');
    if (!pageId) {
        return null;
    }

    const eventSource = new EventSource(renderEventsUrl(pageId, pageVersion));
    eventSource.addEventListener('renderReady', event => {
        const renderReady = parseJSON(event.data);
        if (!renderReady || (pageVersion && String(renderReady.version) !== String(pageVersion))) {
            return;
        }

        eventSource.close();
        fetch(location.href, {
            headers: {
                Accept: 'text/html',
            },
        }).then(response => response.text())
            .then(html => {
                const replacementMain = replacePageContent({document, html, parseHtml});
                if (replacementMain) {
                    onLoaded(replacementMain);
                }
            })
            .catch(error => LOGGER.error('Unable to load rendered page content', error));
    });

    eventSource.onerror = error => LOGGER.debug('Page render-ready stream error', error);
    return eventSource;
};

export const setupRootPageReady = ({
    document = window.document,
    EventSource = window.EventSource,
    location = window.location,
} = {}) => {
    const pending = document.querySelector('[data-root-page-wait="true"]');
    const eventsUrl = pending?.getAttribute('data-root-page-events-url');
    if (!eventsUrl || !EventSource) {
        return null;
    }

    let rootReady = false;
    const eventSource = new EventSource(eventsUrl);
    eventSource.addEventListener('rootReady', event => {
        if (rootReady || parseJSON(event.data) !== true) {
            return;
        }

        rootReady = true;
        eventSource.close();
        location.replace('/pages');
    });
    eventSource.onerror = error => LOGGER.debug('Root Page readiness stream error', error);
    return eventSource;
};
